import React, { useEffect, useState } from "react";
import { Button, CreditCardLogo, SelectRow, TextButton } from "kubra-ux-forge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuildingColumns,
  faCreditCard,
  faMoneyBill,
} from "@fortawesome/pro-regular-svg-icons";

import ApplePayImg from "assets/icons/applepay.svg";
import GooglePayImg from "assets/icons/googlepay.svg";
import PayPalImg from "assets/icons/paypal.svg";
import VenmoImg from "assets/icons/venmo.svg";
import { useTranslation } from "react-i18next";
import { SELECT_PAYMENT_METHOD_MODAL_NAMESPACE } from "constants/i18n-namespaces";
import FundingOption, {
  isPaymentMethodAllowed,
  ApplePay,
  BankAccount,
  Card,
  getCardNetworkByShortCode,
  GooglePay,
  PayPal,
  translateBankAccountType,
  Venmo,
} from "constants/FundingOption";
import _ from "lodash";
import { MethodComponentsOptions } from "../../payable-dashboard/payable-dashboard";
import { useDispatch, useSelector } from "react-redux";
import { getPaymentIntent } from "selectors/paymentIntent";
import { getBiller } from "selectors";
import { DigitalPaymentData } from "types/digitalPaymentData";
import { PaymentIntentPrepareRequest } from "types/Api/Payment/paymentIntentPrepareRequest";
import useWallet from "hooks/useWallet";
import { ACTION } from "actions/paymentIntentActions";
import { WalletItemResponse } from "types/Api/Wallet/walletItemResponse";
import noFundingSourceImage from "assets/no-funding-source-available.svg";
import { getPayableSession } from "selectors/payableSession";
import { phoneFormat } from "utilities/helper";

export const MAXIMUM_WALLET_ITEMS = 3;

export interface ISelectPaymentMethodModalProps {
  flowCallback: (arg0: MethodComponentsOptions) => any;
  closeModalCallBack: () => void;
  showManagePaymentMethodsModal: () => void;
  selectedPaymentMethodId?: string;
  setIsCashView?: (isCashView: boolean) => void;
  reviewDigitalPayment: (paymentData: DigitalPaymentData) => void;
  verifyPaymentAmount: () => { isValid: boolean };
  onPrepareIntentError: (
    err: any,
    prepareRequest: PaymentIntentPrepareRequest
  ) => void;
}

export const SelectPaymentMethodModal = (
  props: ISelectPaymentMethodModalProps
) => {
  const {
    flowCallback,
    closeModalCallBack,
    showManagePaymentMethodsModal,
    selectedPaymentMethodId,
    setIsCashView,
  } = props;

  const { t } = useTranslation([SELECT_PAYMENT_METHOD_MODAL_NAMESPACE]);
  const [isAppleDevice, setIsAppleDevice] = useState(false);

  const dispatch = useDispatch();
  const { getWalletItems } = useWallet(true);

  // TODO: sometimes there is a noticable delay before the wallet item are displayed when this
  // modal is opened because that's exactly when the API call is made i.e when the hook is used first.
  // Consider making the API call when we land on payable dashboard to speed things up?
  const walletItems = getWalletItems();
  const biller = useSelector(getBiller);
  const payableSession = useSelector(getPayableSession);

  const handleWalletItemClick = (selectedWalletItem: WalletItemResponse) => {
    if (selectedWalletItem) {
      dispatch({ type: ACTION.ADD_CREDIT_DEBIT_CARD, payload: null });
      dispatch({ type: ACTION.ADD_BANK_ACCOUNT, payload: null });
      dispatch({ type: ACTION.ADD_WALLET, payload: selectedWalletItem });

      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.Wallet,
      });

      closeModalCallBack();
    }
  };

  const handleDigitalWalletClick = (digitalWallet: any) => {
    dispatch({ type: ACTION.ADD_CREDIT_DEBIT_CARD, payload: null });
    dispatch({ type: ACTION.ADD_BANK_ACCOUNT, payload: null });
    dispatch({ type: ACTION.ADD_WALLET, payload: null });

    if (digitalWallet == FundingOption.GooglePay) {
      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.GooglePay,
      });
    } else if (digitalWallet == FundingOption.ApplePay) {
      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.ApplePay,
      });
    } else if (digitalWallet == FundingOption.PayPal) {
      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.PayPal,
      });
    } else if (digitalWallet == FundingOption.Venmo) {
      dispatch({
        type: ACTION.ADD_PAYMENT_METHOD,
        payload: FundingOption.Venmo,
      });
    }

    closeModalCallBack();
  };

  const payableFsOptions = payableSession.fundingOptions;

  // Detect if the user is on an Apple device
  useEffect(() => {
    const isApple = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
    setIsAppleDevice(isApple);
  }, []);

  const contactUs: any = biller?.properties?.find((element: any) => {
    return element.name === "InstanceConfiguration";
  });

  // No funding source available
  if (payableFsOptions.length === 0) {
    return (
      <div
        data-testid="no-funding-source-available-div"
        className="payment-method-frame"
      >
        <img
          data-testid="no-funding-source-image"
          src={noFundingSourceImage}
          alt="no funding source available"
        ></img>
        <div className="no-funding-source-available">
          {t("no-funding-source-available", {
            biller: biller.billerName,
            phone: phoneFormat(contactUs.properties.phone),
          })}
        </div>
      </div>
    );
  }

  const hasAnyDigitalPaymentMethod =
    isPaymentMethodAllowed(ApplePay, payableFsOptions) ||
    isPaymentMethodAllowed(GooglePay, payableFsOptions) ||
    isPaymentMethodAllowed(PayPal, payableFsOptions) ||
    isPaymentMethodAllowed(Venmo, payableFsOptions);

  return (
    <div className="select-paymentmethod-container">
      <div className="method-buttons-container">
        {isPaymentMethodAllowed(BankAccount, payableFsOptions) && (
          <Button
            className="method-button"
            icon={
              <FontAwesomeIcon
                icon={faBuildingColumns}
                style={{ color: "#007FAD" }}
              />
            }
            onClick={() => {
              setIsCashView && setIsCashView(false);
              flowCallback("PayableBankAccountModal");
            }}
            variant="tonal"
          >
            {t("bank")}
          </Button>
        )}

        {isPaymentMethodAllowed(Card, payableFsOptions) && (
          <Button
            className="method-button"
            icon={
              <FontAwesomeIcon
                icon={faCreditCard}
                style={{ color: "#007FAD" }}
              />
            }
            onClick={() => {
              setIsCashView && setIsCashView(false);
              flowCallback("PayableCreditDebitModal");
            }}
            variant="tonal"
          >
            {t("card")}
          </Button>
        )}

        {payableSession?.payableType?.allowCashPayment && (
          <Button
            className="method-button"
            icon={
              <FontAwesomeIcon
                icon={faMoneyBill}
                style={{ color: "#007FAD" }}
              />
            }
            onClick={() => {
              setIsCashView && setIsCashView(true);
              closeModalCallBack();
            }}
            variant="tonal"
          >
            {t("cash")}
          </Button>
        )}
      </div>

      <div className="wallet-items-container">
        {_.sortBy(
          walletItems,
          (item) => -new Date(item.enrolledDate).getTime()
        ).map((walletItem) => {
          const isSelected =
            selectedPaymentMethodId === walletItem.fundingSourceId;
          const extraStyle = isSelected
            ? {
                borderRadius: "8px",
                border: "3px solid var(--primary-primary-60, #279ACE)",
                background: "var(--primary-primary-95, #464748)",
              }
            : {};

          if (
            isPaymentMethodAllowed(BankAccount, payableFsOptions) &&
            walletItem.fundingOption === BankAccount
          ) {
            return (
              <SelectRow
                key={walletItem.fundingSourceId}
                icon={
                  <FontAwesomeIcon icon={faBuildingColumns}></FontAwesomeIcon>
                }
                label={`${translateBankAccountType(
                  walletItem.presentableMOPCode,
                  t
                )} ${walletItem.maskedNumber}`}
                /* @ts-ignore */
                className="wallet-item-row"
                variant="static"
                style={extraStyle}
                dataCy={`payment-method-row-${walletItem.presentableMOPCode} ${walletItem.maskedNumber}`}
                onClick={() => {
                  setIsCashView && setIsCashView(false);
                  handleWalletItemClick(walletItem);
                }}
              />
            );
          } else if (
            isPaymentMethodAllowed(Card, payableFsOptions) &&
            walletItem.fundingOption === Card
          ) {
            return (
              <SelectRow
                key={walletItem.fundingSourceId}
                icon={
                  <CreditCardLogo
                    cardType={_.lowerCase(
                      getCardNetworkByShortCode(walletItem.presentableMOPCode)
                    )}
                  />
                }
                label={`${getCardNetworkByShortCode(
                  walletItem.presentableMOPCode
                )} ${walletItem.maskedNumber}`}
                /* @ts-ignore */
                className="wallet-item-row"
                variant="static"
                style={extraStyle}
                dataCy={`payment-method-row-${getCardNetworkByShortCode(
                  walletItem.presentableMOPCode
                )} ${walletItem.maskedNumber}`}
                onClick={() => {
                  setIsCashView && setIsCashView(false);
                  handleWalletItemClick(walletItem);
                }}
              />
            );
          }
          return null;
        })}
      </div>

      {walletItems.length > 0 ? (
        <div className="manage-payment-methods-container">
          <TextButton
            onClick={() => {
              closeModalCallBack();
              showManagePaymentMethodsModal();
            }}
          >
            {t("manage-payment-methods-button")}
          </TextButton>
        </div>
      ) : null}

      {hasAnyDigitalPaymentMethod ? (
        <>
          <div className="or-separator-container">
            <span className="or-separator-element"> {t("or")}</span>
          </div>

          <div className="digital-methods-container">
            <div className="digital-method-button-container">
              {/* Show ApplePay button only if it's an Apple device */}
              {isAppleDevice &&
                isPaymentMethodAllowed(ApplePay, payableFsOptions) &&
                isPaymentMethodAllowed(Card, payableFsOptions) && (
                  <Button
                    className="digital-method-button"
                    onClick={() => handleDigitalWalletClick(ApplePay)}
                  >
                    <img src={ApplePayImg} alt="ApplePay" />
                  </Button>
                )}

              {isPaymentMethodAllowed(GooglePay, payableFsOptions) &&
                isPaymentMethodAllowed(Card, payableFsOptions) && (
                  <Button
                    className="digital-method-button"
                    onClick={() => handleDigitalWalletClick(GooglePay)}
                  >
                    <img src={GooglePayImg} alt="GooglePay" />
                  </Button>
                )}
            </div>

            <div className="digital-method-button-container">
              {isPaymentMethodAllowed(PayPal, payableFsOptions) && (
                <Button
                  className="digital-method-button"
                  onClick={() => handleDigitalWalletClick(PayPal)}
                >
                  <img src={PayPalImg} alt="PayPal" />
                </Button>
              )}

              {isPaymentMethodAllowed(Venmo, payableFsOptions) && (
                <Button
                  className="digital-method-button"
                  onClick={() => handleDigitalWalletClick(Venmo)}
                >
                  <img src={VenmoImg} alt="Venmo" />
                </Button>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};
